
/**
 * Module dependencies.
 */

import { ResellerProps } from 'src/types/contacts';
import { color, media, units } from 'src/styles/utils';
import { convertCountriesToLocale } from 'src/core/utils/countries';
import { fetchPage } from 'src/api/app/pages/fetch-page';
import { fetchResellers } from 'src/api/app/resellers/fetch-resellers';
import { getApiPageSlugByLocale, useLocalizedRoute } from 'src/core/utils/routes';
import { getHrefLangs } from 'src/core/utils/href-langs';
import { ifProp, theme } from 'styled-tools';
import { normalizeNewLine } from 'src/core/utils/strings';
import { useInView } from 'react-intersection-observer';
import { useRouter } from 'next/router';
import { useTranslate } from 'src/core/utils/translator';
import CommercialContacts from 'src/components/contacts/commercial-contacts';
import Container from 'src/components/core/layout/container';
import IconButton from 'src/components/core/buttons/icon-button';
import Input from 'src/components/core/forms/fields/input';
import Loading from 'src/components/core/loading';
import Map from 'src/components/core/maps';
import Metatags from 'src/components/metatags';
import ParallaxFadeInUp from 'src/components/core/animations/parallax-fade-in-up';
import RawHtml from 'src/components/core/raw-html';
import React, { ReactElement, useCallback, useMemo, useState } from 'react';
import Select from 'src/components/core/select';
import Type from 'src/components/core/typography';
import arrowRightIcon from 'src/assets/svg/arrow-right.svg';
import find from 'lodash/find';
import head from 'lodash/head';
import lowerCase from 'lodash/lowerCase';
import map from 'lodash/map';
import orderBy from 'lodash/orderBy';
import styled, { css } from 'styled-components';
import uniqBy from 'lodash/uniqBy';
import useNavbarTheme from 'src/context/navbar-theme/use-navbar-theme';
import usePage from 'src/api/app/pages/use-page';
import useResellers from 'src/api/app/resellers/use-resellers';

/**
 * `PageProps` type.
 */

type PageProps = {
  description: string,
  descriptionSeo?: string | null,
  slug: string,
  subtitle?: string | null,
  title: string,
  titleSeo?: string | null,
  translations: Record<string, unknown>[]
};

/**
 * `Props` type.
 */

type Props = {
  data: PageProps,
  resellers: ResellerProps[],
  slug: string
};

/**
 * `Section` styled component.
 */

const Section = styled.section<{ isLoading: boolean }>`
  background-color: ${color('beige200')};
  overflow: hidden;
  padding: ${theme('dimensions.navbarHeightMobile')}px 0 ${units(13)};
  position: relative;

  ${ifProp('isLoading', css`
    min-height: 100vh;
  `)}

  ${media.min('ms')`
    padding: ${theme('dimensions.navbarHeight')}px 0 ${units(11)};
  `}
`;

/**
 * `StyledContainer` styled component.
 */

const StyledContainer = styled(Container)`
  ${media.min('ms')`
    padding: 0;
  `}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  display: grid;
  grid-template-areas:
    'title'
    'desc'
    'selects'
    'map';
  grid-template-columns: 100%;
  grid-template-rows: repeat(4, max-content);

  ${media.min('ms')`
    grid-template-areas:
      '. . title   title title .   .'
      '. . desc    desc  .     .   .'
      '. . selects .     map   map map';
    grid-template-columns: ${theme('grid.gutter')}px 1fr 4fr 1fr 5fr 1fr ${theme('grid.gutter')}px;
    grid-template-rows: repeat(3, max-content);
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H1)`
  grid-area: title;
  margin-bottom: ${units(3)};
  padding-top: ${units(5)};

  ${media.min('ms')`
    margin: 0;
    padding-top: ${units(10)};
  `}
`;

/**
 * `SubTitle` styled component.
 */

const SubTitle = styled(Type.H5).attrs({
  as: 'span'
})`
  display: block;
  grid-area: subTitle;
  padding-top: ${units(1)};

  ${media.min('ms')`
    padding-top: ${units(2)};
  `}
`;

/**
 * `DescriptionWrapper` styled component.
 */

const DescriptionWrapper = styled.div`
  grid-area: desc;
`;

/**
 * `Description` styled component.
 */

const Description = styled(Type.H6).attrs({ as: 'p', small: true })`
  color: ${color('grey400')};
  margin-bottom: ${units(5)};

  ${media.min('ms')`
    margin-bottom: ${units(10)};
  `}
`;

/**
 * `SecondaryTitle` styled component.
 */

const SecondaryTitle = styled(Type.H5).attrs({ as: 'p' })`
  font-weight: 400;
  margin-bottom: ${units(5)};

  ${media.min('ms')`
    margin-bottom: ${units(6)};
  `}
`;

/**
 * `SelectsWrapper` styled component.
 */

const SelectsWrapper = styled.div`
  grid-area: selects;

  ${media.max('ms')`
    margin-bottom: ${units(8)};
  `}
`;

/**
 * `SelectLabel` styled component.
 */

const SelectLabel = styled(Type.H6).attrs({ as: 'p' })`
  margin-bottom: ${units(1.5)};
`;

/**
 * `StyledSelect` styled component.
 */

const StyledSelect = styled(Select)`
  margin-bottom: ${units(5)};

  ${media.min('ms')`
    margin-bottom: ${units(6)};
  `}
`;

/**
 * `InputWrapper` styled component.
 */

const InputWrapper = styled.div`
  align-items: center;
  display: flex;
  position: relative;
`;

/**
 * `Icon` styled component.
 */

const Icon = styled(IconButton)`
  position: absolute;
  right: 2px;
  text-decoration: none;
  top: ${units(6)};
`;

/**
 * `Separator` styled component.
 */

const Separator = styled(Type.H6).attrs({ as: 'p' })`
  margin-bottom: ${units(5)};

  ${media.min('ms')`
    margin-bottom: ${units(6)};
  `}
`;

/**
 * `MapWrapper` styled component.
 */

const MapWrapper = styled.div`
  grid-area: map;
  position: relative;

  > div {
    bottom: 0;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
  }

  ${media.max('ms')`
    height: 105vw;
  `}
`;

/**
 * `WhereToBuy` page.
 */

const WhereToBuy = (props: Props): ReactElement => {
  const { locale, translate } = useTranslate();
  const routeResolver = useLocalizedRoute();
  const router = useRouter();
  const {
    data: pageData,
    isLoading: isLoadingPage,
    isSuccess: isSuccessPage
  } = usePage<PageProps>({
    initialData: props.data,
    slug: props.slug
  });

  const {
    data: resellersData,
    isLoading: isLoadingResellers,
    isSuccess: isSuccessResellers
  } = useResellers<ResellerProps[]>({ initialData: props?.resellers });

  const { countries, defaultCountry, districts } = useMemo(() => {
    const availableCountries = uniqBy(
      map(resellersData, ({ country }) => country),
      'alpha2'
    );

    const availableDistricts = uniqBy(
      map(resellersData, ({ district }) => district),
      'id'
    );

    const normalizedAlpha2Codes = map(availableCountries, ({ alpha2 }) => alpha2);
    const translatedCountries = convertCountriesToLocale(normalizedAlpha2Codes, locale);
    const countries = map(translatedCountries, ({ alpha2, name }) => ({
      label: name,
      value: alpha2
    }));

    const orderedCountries = orderBy(countries, [({ label }) => lowerCase(label)], ['asc']);
    const districts = map(availableDistricts, ({ id, name }) => ({
      label: name,
      value: id
    }));

    const orderedDistricts = orderBy(districts, [({ label }) => lowerCase(label)], ['asc']);
    const hasPortugal = find(countries, ['value', 'PT']);
    const defaultCountry = hasPortugal ? head(countries) : {};

    return {
      countries: orderedCountries,
      defaultCountry,
      districts: orderedDistricts
    };
  }, [locale, resellersData]);

  const isLoading = isLoadingPage || isLoadingResellers;
  const isSuccess = isSuccessPage && isSuccessResellers;
  const [locationInputValue, setLocationInputValue] = useState('');
  const [country, setCountry] = useState<string>('PT');
  const subtitle = pageData?.subtitle;
  const title = pageData?.title;
  const description = pageData?.description;
  const handleFilter = useCallback((label, filterKey, filterValue) => {
    const href = routeResolver('whereToBuyResults', {
      filterType: filterKey,
      filterValue,
      pageLabel: label
    });

    router.push(href, undefined, { locale });
  }, [locale, routeResolver, router]);

  const normalizedMarkers = useMemo(() => {
    return map(resellersData, ({ id, latitude, longitude, name }) => {
      return {
        coordinates: {
          lat: Number(latitude),
          lng: Number(longitude)
        },
        id,
        name
      };
    }) ?? [];
  }, [resellersData]);

  const [ref, inView] = useInView({
    threshold: 0,
    triggerOnce: true
  });

  useNavbarTheme({ theme: 'dark' });

  return (
    <>
      <Metatags
        description={pageData?.descriptionSeo || description}
        hrefLangs={getHrefLangs(pageData?.translations, 'whereToBuy')}
        pageTitle={title}
        title={pageData?.titleSeo}
      />

      <Section
        isLoading={isLoading}
        ref={ref}
      >
        <Loading active={isLoading} />

        {isSuccess && (
          <>
            <StyledContainer>
              <Grid>
                <Title>
                  <ParallaxFadeInUp parallaxOptions={{ speed: 1 }}>
                    {`${title} `}

                    {subtitle && (
                      <SubTitle>
                        {subtitle}
                      </SubTitle>
                    )}
                  </ParallaxFadeInUp>
                </Title>

                <DescriptionWrapper>
                  <ParallaxFadeInUp parallaxOptions={{ speed: 2 }}>
                    <RawHtml element={Description}>
                      {normalizeNewLine(description)}
                    </RawHtml>
                  </ParallaxFadeInUp>
                </DescriptionWrapper>

                <SelectsWrapper>
                  <SecondaryTitle>
                    <ParallaxFadeInUp parallaxOptions={{ speed: 1 }}>
                      {translate('stores:resellersTitle')}
                    </ParallaxFadeInUp>
                  </SecondaryTitle>

                  <SelectLabel id={'resellers_label'}>
                    {translate('forms:resellersLabel')}
                  </SelectLabel>

                  <StyledSelect
                    aria-label={translate('forms:resellersLabel')}
                    disabled={isLoadingResellers}
                    name={'resellers'}
                    onChange={e => handleFilter(translate('stores:resultsLabelReseller'), 'id', e.value)}
                    options={map(resellersData, ({ id, name }) => ({
                      label: name,
                      value: id
                    }))}
                    placeholder={translate('forms:selectPlaceholder')}
                  />

                  <Separator>
                    {translate('stores:orSeparator')}
                  </Separator>

                  <SecondaryTitle>
                    {translate('stores:locationTitle')}
                  </SecondaryTitle>

                  {countries && (
                    <>
                      <SelectLabel id={'country_label'}>
                        {translate('forms:countryLabel')}
                      </SelectLabel>

                      <StyledSelect
                        defaultValue={defaultCountry}
                        name={'country'}
                        onChange={e => {
                          setCountry(e.value);
                        }}
                        options={countries}
                        placeholder={translate('forms:selectPlaceholder')}
                      />
                    </>
                  )}

                  {country === 'PT' && districts ? (
                    <>
                      <SelectLabel id={'district_label'}>
                        {translate('forms:districtLabel')}
                      </SelectLabel>

                      <StyledSelect
                        name={'district'}
                        onChange={e => handleFilter(e.label, 'district', e.value)}
                        options={districts}
                        placeholder={translate('forms:selectPlaceholder')}
                      />
                    </>
                  ) : (
                    <Input
                      disabled={isLoading}
                      label={translate('forms:districtLabel')}
                      name={'district'}
                      onKeyPress={e => {
                        if (e.key === 'Enter') {
                          handleFilter(
                            `"${e.target.value}"`,
                            'place',
                            e.target.value
                          );
                        }
                      }}
                      placeholder={translate('forms:districtPlaceholder')}
                    />
                  )}

                  <InputWrapper>
                    <Input
                      disabled={isLoading}
                      helpText={translate('forms:locationHelpText')}
                      icon={arrowRightIcon}
                      label={translate('forms:locationLabel')}
                      name={'location'}
                      onChange={e => setLocationInputValue(e.target.value)}
                      onKeyPress={e => {
                        if (e.key === 'Enter') {
                          handleFilter(
                            `"${e.target.value}"`,
                            'address',
                            locationInputValue
                          );
                        }
                      }}
                      placeholder={translate('forms:locationPlaceholder')}
                      value={locationInputValue}
                    />

                    <Icon
                      aria-label={translate('common:actions.search')}
                      icon={arrowRightIcon}
                      iconSize={units(3.5)}
                      onClick={() =>
                        handleFilter(
                          translate('stores:resultsLabelLocation'),
                          'address',
                          locationInputValue
                        )
                      }
                    />
                  </InputWrapper>
                </SelectsWrapper>

                <MapWrapper>
                  <ParallaxFadeInUp
                    parallaxOptions={{
                      percentage: 0.1,
                      speed: 2
                    }}
                  >
                    <Map
                      center={{
                        lat: 39.557191,
                        lng: -7.8536599
                      }}
                      isInView={inView}
                      markers={normalizedMarkers}
                      zoom={4}
                    />
                  </ParallaxFadeInUp>
                </MapWrapper>
              </Grid>
            </StyledContainer>
          </>
        )}
      </Section>

      <CommercialContacts />
    </>
  );
};

/**
 * Export `getServerSideProps`.
 */

export async function getServerSideProps({ locale, query }) {
  const slug = getApiPageSlugByLocale('whereToBuy', locale);
  const pageData = await fetchPage({
    hasPreview: query?.preview,
    locale,
    slug
  });

  if (!pageData) {
    return {
      notFound: true
    };
  }

  return {
    props: {
      data: pageData,
      resellers: await fetchResellers({ locale }),
      slug
    }
  };
}

/**
 * Export `WhereToBuy` page.
 */

export default WhereToBuy;
